/**
 * Determines the API URL based on the environment.
 *
 * @returns {string} The appropriate API URL for the current environment
 */
const getApiUrl = (): string => {
    // Local development environment (localhost)
    if (process.env.REACT_APP_ENV === "local") {
        return "http://127.0.0.1:8080";
    }

    // Test/staging environment
    if (process.env.REACT_APP_ENV === "development") {
        return "https://test-api.thesurfr.app";
    }

    // Production environment (default)
    return "https://api.thesurfr.app";
};

// Export the API URL for use throughout the application
export const API_URL = getApiUrl();

const getChatApiUrl = (): string => {
    if (process.env.REACT_APP_CHAT_API_URL) {
        return process.env.REACT_APP_CHAT_API_URL;
    }
    if (process.env.REACT_APP_ENV === "local") {
        return "http://127.0.0.1:4001";
    }
    if (process.env.REACT_APP_ENV === "development") {
        return "https://surfr-claude-chat-dot-kiter-test.appspot.com";
    }
    return "https://surfr-claude-chat-dot-kiter-271715.appspot.com";
};

export const CHAT_API_URL = getChatApiUrl();

/**
 * live-wind Worker (Cloudflare) — read-only station readings. The admin browser talks to it
 * DIRECTLY (its origin is on the Worker's CORS allowlist); nothing is proxied through the
 * classic backend. Local dev uses the test Worker.
 */
const getLiveWindUrl = (): string => {
    if (process.env.REACT_APP_LIVE_WIND_URL) {
        return process.env.REACT_APP_LIVE_WIND_URL;
    }
    if (process.env.REACT_APP_ENV === "local" || process.env.REACT_APP_ENV === "development") {
        return "https://test-live-wind.thesurfr.app";
    }
    return "https://live-wind.thesurfr.app";
};

export const LIVE_WIND_URL = getLiveWindUrl();

/**
 * True when this build targets a test environment (localhost or the test
 * backend). Test-only surfaces (e.g. the Test Data admin page) are only
 * registered when this is true; the backend additionally 404s the
 * corresponding proxy endpoints outside STAGE=TEST (two layers of gating).
 */
export const IS_TEST_ENV =
    process.env.REACT_APP_ENV === "local" || process.env.REACT_APP_ENV === "development";

export const JWT_KEY = "user-object";

export const ENDPOINTS = {
    // live-wind Worker /v1 (absolute — not axiosInstance) — SRF-2855
    LIVE_WIND: {
        STATION: (id: string) => `${LIVE_WIND_URL}/v1/stations/${encodeURIComponent(id)}`,
        NEAREST: (lat: number, lon: number, limit = 10) =>
            `${LIVE_WIND_URL}/v1/stations?lat=${lat}&lon=${lon}&limit=${limit}`,
    },
    AUTH: {
        LOGIN: `/users`,
        SEND_CODE: `/auth/signin/send-verification-code`,
        VERIFY_CODE: `/auth/signin/verify-code`,
        RESEND_CODE: `/auth/signin/resend-verification-code`,
    },
    // this two endpoints are not using axiosInstance so we need to add the backend url
    COMPETITIONS: {
        DEFAULT: `${API_URL}/competition`,
        LIVEDATA: `${API_URL}/competition/livedata`,
    },
    IMAGES: {
        PROFILE: `${API_URL}/image/profile`,
    },
    USERS: {
        SEARCH: `/users/list/0?limit=5&newversion=${true}`,
    },
    WHATSNEW: {
        DEFAULT: "/whatsnew",
    },
    FREESUBSCRIPTIONS: {
        DEFAULT: "/free-subscription",
        ALL: "/free-subscription/all",
        END: "/free-subscription/end-now",
        // Stripe-billed subscriptions — read-only here; Stripe is their source of
        // truth, so the normal list/update/end endpoints exclude and reject them.
        STRIPE: "/free-subscription/stripe",
        // Dead-letter view: webhook events Stripe delivered but processing couldn't complete.
        STRIPE_ERRORS: "/free-subscription/stripe/webhook-errors",
        // Live re-fetch from the Stripe API for one user; append /{userId}.
        STRIPE_RECONCILE: "/free-subscription/stripe/reconcile",
    },
    GEAR: {
        LIST: "/userkites/list",
        CREATE: "/userkites",
        UPDATE: (id: string) => `/userkites/${id}`,
        DELETE: (id: string) => `/userkites/${id}`,
        BRANDS: "/kitebrands",
        BRANDS_PAGINATED: "/kitebrands/paginated",
        BRANDS_UPDATE: (id: number) => `/kitebrands/${id}`,
        BRANDS_DELETE: (id: number) => `/kitebrands/${id}`,
        MODELS: "/kitemodels",
        MODELS_PAGINATED: "/kitemodels/paginated",
        MODELS_UPDATE: (id: number) => `/kitemodels/${id}`,
        MODELS_DELETE: (id: number) => `/kitemodels/${id}`,
        VERSIONS: "/kiteversions",
        VERSIONS_LIST: "/kiteversions/list/0",
        VERSIONS_PAGINATED: "/kiteversions/paginated",
        VERSIONS_UPDATE: (id: number) => `/kiteversions/${id}`,
        VERSIONS_DELETE: (id: number) => `/kiteversions/${id}`,
        TYPES: "/gears/types",
    },
    ADVERTISEMENTS: "/advertisements",
    ADMIN: {
        USERS: "/admin/users",
        UPDATE_EMAIL: (userId: number) => `/admin/users/${userId}/email`,
        ASSIGN_SESSION: (sessionId: number) => `/admin/sessions/${sessionId}/assign`,
        UPDATE_SESSION_DATETIME: (sessionId: number) => `/admin/sessions/${sessionId}/datetime`,
        DETECT_SESSION_TIME_JUMP: (sessionId: number) => `/admin/sessions/${sessionId}/time-jump`,
        REPAIR_SESSION_TIME_JUMP: (sessionId: number) => `/admin/sessions/${sessionId}/repair-time-jump`,
        FIT_DOWNLOAD: (sessionId: number) => `/admin/sessions/${sessionId}/fit-download`,
        USER_DEVICES: (userId: number) => `/admin/users/${userId}/devices`,
        USER_KITES: (userId: number) => `/admin/users/${userId}/kites`,
        UPDATE_COMMENT: (commentId: number) => `/admin/comments/${commentId}`,
        DELETE_COMMENT: (commentId: number) => `/admin/comments/${commentId}`,
        GARMIN_FILES: (userId: number) => `/admin/users/${userId}/garmin-files`,
        GARMIN_FILE_DOWNLOAD: (userId: number, activityId: string) => `/admin/users/${userId}/garmin-files/${activityId}/download`,
        GDPR_EXPORT: (userId: number) => `/admin/users/${userId}/gdpr-export`,
        RECONCILE_SUBSCRIPTION: (userId: number) => `/admin/users/${userId}/reconcile-subscription`,
        SPOTS_SEARCH: "/admin/spots/search",
        SPOT_IMAGES: {
            BASE: (spotId: number) => `/admin/spots/${spotId}/images`,
            ORDER: (spotId: number) => `/admin/spots/${spotId}/images/order`,
            COVER: (spotId: number) => `/admin/spots/${spotId}/images/cover`,
            HIDE: (spotId: number, filename: string) => `/admin/spots/${spotId}/images/${filename}`,
            RESTORE: (spotId: number, filename: string) => `/admin/spots/${spotId}/images/${filename}/restore`,
            DELETE_PERMANENT: (spotId: number, filename: string) => `/admin/spots/${spotId}/images/${filename}/permanent`,
            RESET: (spotId: number) => `/admin/spots/${spotId}/images/reset`,
        },
        SYSTEM: {
            OVERVIEW: "/api/admin/system/overview",
            SERVICES: "/api/admin/system/services",
            JOBS: "/api/admin/system/jobs",
            INTEGRATIONS: "/api/admin/system/integrations",
            DATABASE: "/api/admin/system/database",
            USERS: "/api/admin/system/users",
            ERRORS: "/api/admin/system/errors",
            METRICS_TIMESERIES: "/api/admin/system/metrics/timeseries",
            SERVICE_TIMESERIES: (service: string) => `/api/admin/system/metrics/timeseries/${service}`,
            SERVICE_ENDPOINTS: (service: string) => `/api/admin/system/services/${service}/endpoints`,
            ENDPOINT_TIMESERIES: (service: string) => `/api/admin/system/services/${service}/endpoints/timeseries`,
            ENDPOINT_ERRORS: (service: string) => `/api/admin/system/services/${service}/endpoints/errors`,
            API_ERRORS: "/api/admin/system/errors/api",
            TRACE_LOGS: (traceId: string) => `/api/admin/system/errors/trace/${traceId}`,
            // APM Trace endpoints
            TRACES: "/api/admin/system/traces",
            TRACES_LOGS: (traceId: string) => `/api/admin/system/traces/${traceId}/logs`,
            TRACES_SPANS: (traceId: string) => `/api/admin/system/traces/${traceId}/spans`,
            TRACES_ANALYZE: "/api/admin/system/traces/analyze",
            TRACES_SEARCH: "/api/admin/system/traces/search",
            TRACES_USER: "/api/admin/system/traces/user",
        },
    },
    LIVE: {
        LIST: (page: number) => `/live/list/${page}`,
        SPOT_STATISTICS: (spotId: number) => `/live/spots/statistics/${spotId}`,
    },
    COUNTRIES: "/countries",
    SPOTS: {
        SEARCH: "/kiteworldmap/spots/query",
    },
    USER_STOKES: {
        GET: (userId: number) => `/user-stokes?userid=${userId}&admin=true`,
        ADJUST: "/user-stokes/adjust",
    },
    WIND_ALERTS: {
        ADMIN_TRIGGER: "/windalerts/admin/trigger",
        ADMIN_TRIGGER_DIGEST: "/windalerts/admin/trigger-digest",
        ADMIN_EVALUATIONS: "/windalerts/admin/evaluations",
    },
    // Live wind stations (public read endpoints on the classic backend).
    WIND_STATIONS: {
        BBOX: "/windystations/bbox",
        SEARCH: "/windystations/search",
        NEAREST: "/windystations/nearest",
        HISTORY: (id: string) => `/windystations/${encodeURIComponent(id)}/history`,
        REFRESH: (id: string) => `/windystations/${encodeURIComponent(id)}/refresh`,
    },
    // Live-wind push-station credentials (admin, KV-backed interim — SRF-2747).
    PUSH_STATIONS: {
        LIST: "/api/admin/push-stations",
        CREATE: "/api/admin/push-stations",
        REVOKE: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/revoke`,
        // Destructive + irreversible (SRF-2803). Requires a {stationId} confirmation body.
        DELETE: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}`,
        REACTIVATE: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/reactivate`,
        CONTACT: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/contact`,
        /** Edit the KV credential record's own fields (name / lat / lon / ratePerMin). */
        STATION: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/station`,
        /** Replace the push credential (new PASSKEY or generated token). */
        CREDENTIAL: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/credential`,
        CLOUDFLARE_ALL: "/api/admin/push-stations/cloudflare/all",
        // SRF-2750 self-service approval queue
        APPROVE: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/approve`,
        REJECT: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/reject`,
        /** Configure/clear pull ingest (we FETCH from a URL). Admin-only by design. */
        PULL: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/pull`,
        /** Pull-format catalogue (SRF-2869): what each provider needs, its bounds + placeholder. */
        PULL_PROVIDERS: "/api/admin/push-stations/pull-providers",
        /** One bounded test fetch of a poll URL. Persists nothing; never echoes the token. */
        PULL_TEST: "/api/admin/push-stations/pull-test",
        /** IANA zone for a location (PostGIS timezone_areas) → {tz} or {tz:null}. */
        TIMEZONE: "/api/admin/push-stations/timezone",
        /** Record data-usage consent on the owner's behalf (requires evidence). */
        CONSENT: (prefix: string) => `/api/admin/push-stations/${encodeURIComponent(prefix)}/consent`,
        /** Rejected-push inspector: aggregated credential rejects (no secrets). */
        REJECTS: "/api/admin/push-stations/rejects",
        /** Arm capture of the next rejected requests matching a prefix / IP. */
        REJECTS_CAPTURE: "/api/admin/push-stations/rejects/capture",
        /** GET captured requests (masked; ?reveal=<id> unmasks one) · DELETE purges them all. */
        REJECTS_CAPTURES: "/api/admin/push-stations/rejects/captures",
    },
    // Test-data simulator proxy (classic backend → surfr-dev-simulator).
    // Only live when the backend runs with STAGE=TEST; 404s everywhere else.
    TEST_DATA: {
        START: "/testdata/start",
        STOP: "/testdata/stop",
        STATUS: "/testdata/status",
        READINESS: "/testdata/readiness",
        INJECT: "/testdata/inject",
        INJECT_DELETE: (id: string) => `/testdata/inject/${id}`,
        WIND: "/testdata/wind",
        FIT: "/testdata/inject-fit",
    },
};
